import { useState, type ReactNode } from 'react';
import PictureImage from './Common/PictureImage';
import FAQSchema from '../seo/FAQSchema';
import './Homepage/FAQ.css';

export type FAQ = { question: string; answer: string | ReactNode; id?: number };

type FAQSectionProps = {
  faqs: FAQ[];
  headingId?: string;
  className?: string;
  title?: string;
  description?: string | ReactNode;
  imageSrc?: string;
  imageAlt?: string;
  showImage?: boolean;
  schemaScriptId?: string;
  nonce?: string;
};

export default function FAQSection({
  faqs,
  headingId = 'faq-heading',
  className = 'faqs-section',
  title = 'Frequently Asked Questions',
  description,
  imageSrc = '/Images/merchent/FAQ_image.webp',
  imageAlt = 'Frequently Asked Questions',
  showImage = true,
  schemaScriptId,
  nonce = 'ppp-2026-gtm-nonce',
}: FAQSectionProps) {
  const safeFaqs = Array.isArray(faqs) ? faqs : [];
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const scriptId = schemaScriptId ?? `${headingId}-schema`;

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (safeFaqs.length === 0) return null;

  return (
    <>
      <FAQSchema
        faqs={safeFaqs.map(({ question, answer }) => ({
          question,
          answer: typeof answer === 'string' ? answer : '',
        }))}
        id={scriptId}
        nonce={nonce}
      />
      <section className={className} aria-labelledby={headingId}>
        <div className="faqs-container">
          <div className="faqs-header">
            <h2 id={headingId} className="faqs-title">
              {title}
            </h2>
            {description ? <p className="faqs-description">{description}</p> : null}
          </div>

          <div
            className={`faqs-content${showImage ? ' faqs-content--with-image' : ' faqs-content--no-image'}`}
          >
            <div className="faqs-list">
              {safeFaqs.map((faq, index) => (
                <div
                  key={faq.id ?? faq.question}
                  className={`faq-item ${openIndex === index ? 'faq-open' : ''}`}
                >
                  <div className="faq-question-wrapper" onClick={() => toggleFAQ(index)}>
                    <h3 className="faq-question">{faq.question}</h3>
                    <button
                      type="button"
                      className={`faq-toggle ${openIndex === index ? 'faq-toggle-open' : ''}`}
                      aria-expanded={openIndex === index}
                      aria-label={openIndex === index ? 'Close answer' : 'Open answer'}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFAQ(index);
                      }}
                    >
                      <svg width="32" height="32" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        {openIndex === index ? (
                          <path d="M5 10H15" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                        ) : (
                          <>
                            <path d="M10 5V15" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                            <path d="M5 10H15" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                  <div className={`faq-answer ${openIndex === index ? 'faq-answer-open' : ''}`}>
                    <p><strong>Answer: </strong>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>

            {showImage ? (
              <div className="faqs-image-wrapper">
                <PictureImage
                  src={imageSrc}
                  alt={imageAlt}
                  className="faqs-image"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </>
  );
}
